import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Box, Loading } from "orcs-design-system";
import styled from "styled-components";
import { isEqual, get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import NodeVisualizer from "src/components/NodeVisualizer";
import usePrevious from "src/util/usePrevious";
import { useReactFlowContext } from "src/contexts/reactFlowContext";
import { useOrgChartContext } from "../../context/OrgChartContext";
import { useOrgChartNodesAndEdges } from "../../hooks/useOrgChartNodesAndEdges";
import OrgChartPanel from "../OrgChartPanel";

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1000;
`;

const TeamVisualiser = ({ shouldUseRemoteControl = true }) => {
  const { state, teamId } = useOrgChartContext();
  const context = useReactFlowContext();
  const prevState = usePrevious(state);
  const prevTeamIdRef = useRef(teamId);
  const [initialFitViewTriggered, setInitialFitViewTriggered] = useState(false);

  const { nodes, edges } = useOrgChartNodesAndEdges({ state });

  // Determine if we're in initial loading state
  const isLoading = useMemo(() => {
    // If teams object is empty, we're definitely loading
    if (isEmpty(state?.teams)) {
      return true;
    }
    // If currentViewTeam doesn't exist yet, we're loading
    if (!state?.currentViewTeam) {
      return true;
    }
    // If currentViewTeam exists but is marked as loading, we're loading
    if (state?.currentViewTeam?.isLoading) {
      return true;
    }
    return false;
  }, [state?.teams, state?.currentViewTeam]);

  useEffect(() => {
    // Reset the initial fit view triggered flag when the teamId changes
    // This enables smooth transitions when navigating between teams
    const teamIdChanged = prevTeamIdRef.current !== teamId;
    if (teamIdChanged) {
      setInitialFitViewTriggered(false);
      prevTeamIdRef.current = teamId;
    }
  }, [teamId]);

  const afterLayout = useCallback(
    ({ reactFlow, nodes: layoutedNodes }) => {
      if (initialFitViewTriggered) {
        return;
      }

      const currentViewNode = layoutedNodes.find(
        (n) => n.id === state.currentViewTeam?.id
      );

      if (!currentViewNode) {
        // Wait for the node to be available before transitioning
        return;
      }

      setInitialFitViewTriggered(true);

      setTimeout(() => {
        const paneRects = document
          .querySelector(".react-flow__renderer")
          ?.getBoundingClientRect();

        if (!paneRects) {
          return;
        }

        reactFlow.setViewport(
          {
            zoom: 0.8,
            y: -30,
            x: paneRects.width / 2 - get(currentViewNode, "width", 400) / 2,
          },
          { duration: 500 }
        );
      }, 100);
    },
    [initialFitViewTriggered, state.currentViewTeam]
  );

  const shouldPreventUpdatingNodes = useCallback(() => {
    return (
      state?.expandedPanel &&
      prevState?.expandedPanel &&
      !isEqual(state?.expandedPanel, prevState?.expandedPanel)
    );
  }, [prevState?.expandedPanel, state]);

  return (
    <Box height="100%" position="relative">
      <OrgChartPanel />
      <NodeVisualizer
        loading={isLoading}
        initialNodes={nodes}
        initialEdges={edges}
        afterLayout={afterLayout}
        animatedNodesEnabled
        shouldPreventUpdatingNodes={shouldPreventUpdatingNodes}
        isFullScreen={context?.isFullScreen}
        toggleFullScreen={context?.toggleFullScreen}
        shouldUseRemoteControl={shouldUseRemoteControl}
      />
      {isLoading && (
        <LoadingOverlay>
          <Loading large centered />
        </LoadingOverlay>
      )}
    </Box>
  );
};

TeamVisualiser.propTypes = {
  shouldUseRemoteControl: PropTypes.bool,
};

export default React.memo(TeamVisualiser);
